/* eslint react-hooks/exhaustive-deps: 0 */
/**
 * Batch E · Round E1 — grey-not-hide guard.
 *
 * On a Review engagement, an audit-only page (Sampling, Vouching,
 * Confirmations, Controls, Audit Response, JE Testing, Opening
 * balances re-run) renders the "Not used in a review" notice + an
 * "Enable for this engagement" button (Partner / Manager, reason
 * required). Once enabled the wrapped children render normally.
 *
 * Audit engagements: children always render (no guard, no delay).
 */
import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Ban, Unlock } from "lucide-react";

export default function AuditOnlyGuard({ pageId, children }) {
  const { currentEngId } = useEngagement();
  const { t } = useI18n();
  const [profile, setProfile] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!currentEngId) return;
    try {
      const r = await api.get(`/engagements/${currentEngId}/profile`, { _silent: true });
      setProfile(r.data);
    } catch (_) { setProfile({ engagement_type: "audit" }); }
  };
  useEffect(() => { load(); }, [currentEngId]);

  if (!currentEngId) return children;
  if (!profile) return <div className="text-sm text-muted-foreground p-6" data-testid={`audit-guard-${pageId}-loading`}>{t("common.loading")}</div>;

  const isReview = profile.engagement_type === "review";
  const enab = (profile.page_enablements || {})[pageId] || {};
  const isEnabled = !isReview || enab.enabled === true;
  if (isEnabled) return children;

  const submit = async (targetEnabled) => {
    if (!reason.trim()) { toast.error(t("review.pageEnableReasonRequired")); return; }
    try {
      setBusy(true);
      await api.post(`/engagements/${currentEngId}/review/pages/enable?page_id=${encodeURIComponent(pageId)}`,
        { enabled: targetEnabled, reason }, { _silent: true });
      setReason("");
      await load();
      toast.success(t(targetEnabled ? "review.pageEnabledOk" : "review.pageDisabledOk"));
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6" data-testid={`audit-guard-${pageId}`}>
      <Card className="border-slate-300 bg-slate-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base normal-case text-slate-900">
            <Ban className="h-4 w-4 text-slate-500" />
            {t("review.notUsed")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-[13px] text-slate-800">
          <div data-testid={`audit-guard-${pageId}-ar`} dir="rtl" className="text-slate-700">
            {t("review.notUsedAr")}
          </div>
          <div className="rounded border bg-white p-3 space-y-2">
            <div className="text-[12px] font-medium">{t("review.enableTitle")}</div>
            <p className="text-[11px] text-muted-foreground">{t("review.enableHint")}</p>
            <Textarea value={reason} onChange={e=>setReason(e.target.value)}
                          placeholder={t("review.enableReasonPh")}
                          className="h-16 text-[11px]"
                          data-testid={`audit-guard-${pageId}-reason`} />
            <Button onClick={()=>submit(true)} disabled={busy}
                        data-testid={`audit-guard-${pageId}-enable`}>
              <Unlock className="h-3 w-3 me-1" />{t("review.enableBtn")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
